import React from "react";

// Cleaned base render (placeholder name/dose removed from the silver label)
const VIAL_IMAGE = "/vial-base.png";
const IMG_W = 1125;
const IMG_H = 1398;

/**
 * Derives the vial label content (peptide name + dose) from the product data.
 * Values are parsed from the product TITLE so any title change automatically
 * updates the rendered vial label.
 */
export const parseVialLabel = (product = {}) => {
  const title = product.name || "";
  // Dose = the LAST parenthetical group containing a digit, e.g. "(10mg)"
  const m = title.match(/\(([^()]*\d[^()]*)\)\s*$/);
  let dose;
  let name = title;
  if (m) {
    dose = m[1].trim();
    name = title.slice(0, m.index).trim();
  } else {
    dose = product.size || "";
  }
  // Normalise dose spacing: "10mg" -> "10 mg", keep mL casing
  dose = dose.replace(/(\d)\s*(mcg|mg|ml|iu|pk|g)\b/i, (_all, d, u) => {
    const unit = /^ml$/i.test(u) ? "mL" : u.toLowerCase();
    return `${d} ${unit}`;
  });

  return { name, dose };
};

// Peptide name is the largest element; scale it down as the name gets longer.
const nameFontSize = (name) => {
  const len = (name || "").length;
  if (len <= 8) return 7.2;
  if (len <= 13) return 5.6;
  if (len <= 20) return 4.2;
  if (len <= 30) return 3.3;
  return 2.7;
};

/**
 * Premium pharmaceutical vial render using the FesX product photograph as the
 * base, with the peptide name + dose overlaid onto the cleaned centre of the
 * brushed-silver label. The overlay is positioned relative to an inner wrapper
 * that matches the photo's aspect ratio, so it always aligns with the label.
 */
const VialRender = ({ product, className = "" }) => {
  const { name, dose } = parseVialLabel(product);
  const nameSize = nameFontSize(name);
  // Non-breaking hyphen keeps names like "BPC-157" on one line while
  // multi-word names (e.g. "CJC-1295 (no DAC) + Ipamorelin") still wrap on spaces.
  const displayName = name.replace(/-/g, "\u2011");

  return (
    <div
      className={`relative w-full h-full flex items-center justify-center overflow-hidden ${className}`}
      style={{ background: "linear-gradient(180deg,#fbfcfd 0%,#eef2f6 100%)" }}
      role="img"
      aria-label={`${name} ${dose} research vial`}
    >
      {/* inner wrapper matches the photo aspect ratio so overlay stays aligned */}
      <div
        className="relative h-full"
        style={{ aspectRatio: `${IMG_W} / ${IMG_H}`, containerType: "size" }}
      >
        <img
          src={VIAL_IMAGE}
          alt={`${name} vial`}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />

        {/* dynamic label text — peptide name + dose, bound to product data */}
        <div
          className="absolute flex flex-col items-center justify-center text-center"
          style={{
            left: "49.6%",
            top: "58.5%",
            transform: "translate(-50%, -50%)",
            width: "32%",
            gap: "1.2cqw",
            fontFamily: "Inter, Helvetica, Arial, sans-serif",
          }}
        >
          <div
            className="font-bold"
            style={{
              fontSize: `${nameSize}cqw`,
              lineHeight: 1.03,
              color: "#0d0f13",
              letterSpacing: "-0.01em",
            }}
          >
            {displayName}
          </div>
          {dose && (
            <div
              className="font-medium"
              style={{ fontSize: "4.6cqw", color: "#0d0f13" }}
            >
              {dose}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VialRender;
